import { PenIcon } from "@xola/icons";
import { Skeleton } from "@xola/ui-kit";
import { SwitchField } from "common";
import pThrottle from "p-throttle";
import React, { useState } from "react";
import { Experience, Product, ProductObject, SelectionState } from "types";
import { ProductSelectorModal } from "../../../Common/ProductSelectorModal";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { SelectedProductList } from "../../../Common/SelectedProductList";
import { api } from "@/api/api";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

export interface CancellationProtectionPreferenceValue {
    enabled: boolean;
    experiences: SelectionState;
}

// One write per second. A seller with hundreds of products would otherwise fire hundreds of writes
// back to back on a single Save, which risks rate limiting and lets the seller's jobs run against
// each other.
const throttle = pThrottle({ limit: 1, interval: 1000 });

const setExperienceEnabled = throttle(async (id: ID, enabled: boolean) =>
    api.post(`/experiences/${id}/cancellationProtection`, { enabled }),
);

const isEligible = (experience: Experience) => experience.cancellationProtection?.eligible ?? false;

export const getEligibleExperienceIds = (experiences: Experience[]) =>
    experiences.filter((experience) => isEligible(experience)).map(({ id }) => id);

/**
 * The selection the seller is looking at: every eligible experience whose own switch is on. An
 * experience's `enabled` defaults to true, so "absent from the selection" has to be written back as
 * an explicit false rather than left alone.
 */
export const getEnabledExperienceSelection = (experiences: Experience[]): SelectionState => {
    const eligible = experiences.filter((experience) => isEligible(experience));
    const enabled = eligible.filter((experience) => experience.cancellationProtection?.enabled);

    return enabled.length > 0 && enabled.length === eligible.length
        ? { all: true, selected: [] }
        : { all: false, selected: enabled.map(({ id }) => id) };
};

export const useCancellationProtectionExperiences = (enabled: boolean) => {
    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>("experiences", null, { enabled });

    return { experiences: experiences ?? [], isFetching };
};

/** Sends one call per experience whose switch actually changes, one at a time. */
export const saveExperienceSelection = async (experiences: Experience[], selection: SelectionState) => {
    const eligibleIds = getEligibleExperienceIds(experiences);
    const current = getEnabledExperienceSelection(experiences);
    const alreadyEnabled = new Set(current.all ? eligibleIds : current.selected);
    const selectedIds = new Set(selection.all ? eligibleIds : selection.selected);

    for (const id of eligibleIds) {
        const shouldEnable = selectedIds.has(id);
        if (shouldEnable !== alreadyEnabled.has(id)) {
            await setExperienceEnabled(id, shouldEnable);
        }
    }
};

interface CancellationProtectionPreferenceFormProps {
    value: CancellationProtectionPreferenceValue;
    experiences: Experience[];
    isFetching: boolean;
    onChange: (value: CancellationProtectionPreferenceValue) => void;
}

export const CancellationProtectionPreferenceForm = ({
    value,
    experiences,
    isFetching,
    onChange,
}: CancellationProtectionPreferenceFormProps) => {
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const eligibleIds = getEligibleExperienceIds(experiences);

    return (
        <div data-testid="cancellation-protection-preferences-section">
            <SwitchField
                className="!items-start"
                isChecked={value.enabled}
                onChange={(enabled: boolean) => onChange({ ...value, enabled })}
            >
                Let customers add cancellation protection to their booking at checkout
            </SwitchField>

            <div className="mt-6 mb-5 flex items-center justify-between">
                <h3>Products</h3>
                <SecondaryButton
                    size="small"
                    disabled={!value.enabled || isFetching}
                    data-testid="cancellation-protection-product-selector-button"
                    onClick={() => setIsSelectorOpen(true)}
                >
                    <PenIcon />
                </SecondaryButton>
            </div>

            {isFetching ? (
                <Skeleton height={160} />
            ) : (
                <SelectedProductList
                    selectedProducts={value.experiences}
                    selectedAllText="Offered on every eligible product"
                    selectedNoneText="Not offered on any product"
                />
            )}

            <ProductSelectorModal
                allowEmptySelection
                hideUnScheduled
                value={value.experiences}
                isOpen={isSelectorOpen}
                title="Cancellation Protection"
                description="Choose the products that offer cancellation protection at checkout"
                objects={[ProductObject.EXPERIENCE]}
                filterProduct={(product: Product) => eligibleIds.includes(product.id)}
                onClose={() => setIsSelectorOpen(false)}
                onConfirm={(experiences: SelectionState) => onChange({ ...value, experiences })}
            />
        </div>
    );
};
